/**
 * round11/password-safeguards — the live checklist under every "new
 * password" field (sign-in's first-time password, the reset screen, the
 * admin plane's first sign-in). Each rule flips from ○ to ✓ as the person
 * types, and a "Both passwords match" line appears once a confirm field is
 * wired in. No colour carries meaning on its own: the glyph and the
 * visually-hidden "(done)" / "(still needed)" text do, and the states use
 * only the existing ink/muted text colours (no new contrast pairing).
 *
 * Not `aria-live`: announcing four lines on every keystroke is noise. The
 * on-submit StatusMessage (role="alert") still names the first unmet rule.
 */
import { evaluatePassword } from "../lib/passwordPolicy";
import styles from "./PasswordRules.module.css";

export function PasswordRules({
  password,
  confirm,
  id,
}: {
  password: string;
  /** When provided, adds a "Both passwords match" line. */
  confirm?: string;
  /** Point the password input's aria-describedby at this. */
  id?: string;
}) {
  const { rules } = evaluatePassword(password);
  const showMatch = confirm !== undefined;
  const matches = showMatch && confirm.length > 0 && confirm === password;

  const line = (key: string, ok: boolean, label: string) => (
    <li key={key} className={ok ? styles.ok : styles.todo} data-ok={ok ? "true" : "false"}>
      <span aria-hidden="true" className={styles.mark}>{ok ? "✓" : "○"}</span>
      {label}
      <span className="sr-only">{ok ? " (done)" : " (still needed)"}</span>
    </li>
  );

  return (
    <ul id={id} className={styles.rules} aria-label="Password requirements">
      {rules.map((r) => line(r.key, r.ok, r.label))}
      {showMatch && line("match", matches, "Both passwords match")}
    </ul>
  );
}
